import { useState, useEffect } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableFooter,
	Paper,
	LinearProgress,
	Box,
	Typography,
	Chip,
	IconButton,
	Button,
	Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PaymentIcon from "@mui/icons-material/Payment";
import reqEmpresaFatura from "../../services/reqMultiTenant/reqEmpresaFatura";
import { toast, ToastContainer } from "react-toastify";
import { handlePdf } from "../../util/reports/receipt";
import FormCreditCard from "./formCreditCard";
import ModalMain from "../../components/modal";
import reqBillingAsaas from "../../services/reqAsaas/reqBilling";
import { BtnCustom } from "../../components/button/button";
import { LibString } from "../../util/libString";
import { isEmptyObj } from "../../util/libObject";

/* ===== Helpers ===== */
// status vindo da API -> rótulo
const statusLabel = (s) => ({ A: "Aberta", P: "Paga", V: "Vencida" })[s] || s;
const statusColor = (s) => (s === "A" ? "warning" : s === "P" ? "success" : s === "V" ? "error" : "default");

// *** Datas SEM “voltar 1 dia” ***
// Formata ISO como data pt-BR, fixando timezone=UTC (evita deslocamento -3h)
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "");
const mesAnoLongo = (iso) =>
	iso ? new Date(iso).toLocaleDateString("pt-BR", { month: "long", year: "numeric", timeZone: "UTC" }) : "";

// Normaliza para comparar apenas Y-M-D em UTC (sem fuso)
const toUTCYMD = (d) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
const isOverdue = (iso) => {
	if (!iso) return false;
	const due = new Date(iso);
	const today = new Date();
	return toUTCYMD(due) < toUTCYMD(today);
};

const fmtMoney = (v) => (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const InvoiceViewer = ({ invoiceData }) => {
	const [dados, setDados] = useState([]); // itens da tabela
	const [carregando, setCarregando] = useState(false);

	const [faturaAtual, setFaturaAtual] = useState(null);
	const [faturasAnteriores, setFaturasAnteriores] = useState([]);
	const [loading, setLoading] = useState(false);

	// verifica se veio do form de configuração ou da pagina fatura
	const [isFormManager, setIsFormManager] = useState(false);

	const { capitalizeFirstLetter } = LibString();

	const opcoes = {
		colunasTabela: [
			{ key: "produto", label: "Produto", align: "left", width: 260 },
			{ key: "valor", label: "Valor", align: "right", format: "currency" },
			{ key: "valor_desconto", label: "Desconto", align: "right", format: "currency" },
			{ key: "acrescimo", label: "Acréscimo", align: "right", format: "currency" },
			{ key: "totalItem", label: "Total Item", align: "right", format: "currency" },
			// { key: "acoes", label: "Ações", align: "center", width: 80 },
		],
	};

	useEffect(() => {
		fetchData();
	}, []);

	const fetchData = async () => {
		try {
			setCarregando(true);

			let responseInvoice;

			//se recebeu a conta mostra se não pesquisa
			if (invoiceData) {
				setIsFormManager(true);

				const data = invoiceData;
				responseInvoice = data;
			} else {
				const { data } = await reqEmpresaFatura.listInvoice();
				responseInvoice = data;
			}

			const faturas = Array.isArray(responseInvoice?.faturas) ? responseInvoice.faturas : [];
			const itens = Array.isArray(responseInvoice?.itens) ? responseInvoice.itens : [];

			// Ordena por vencimento desc
			const ordenadas = [...faturas].sort((a, b) => new Date(b.data_vencimento) - new Date(a.data_vencimento));

			// Fatura "aberta" vinda da API
			const abertaAPI = ordenadas.find((f) => f.status === "A") || null;

			// Se existe aberta e está vencida hoje (venc < hoje), marcamos como VENCIDA
			if (abertaAPI) {
				const vencida = isOverdue(abertaAPI.data_vencimento);
				const statusRawComputado = vencida ? "V" : "A";

				setFaturaAtual({
					id: abertaAPI.id_fatura,
					statusRaw: statusRawComputado,
					id_cobranca_asaas: abertaAPI.id_cobranca_asaas,
					status: statusLabel(statusRawComputado),
					vencimentoISO: abertaAPI.data_vencimento,
					vencimento: fmtDate(abertaAPI.data_vencimento),
					mesExibicao: capitalizeFirstLetter(mesAnoLongo(abertaAPI.data_vencimento)),
					pagoEm: fmtDate(abertaAPI.data_pagto),
					valor: abertaAPI.valor_fatura,
					valorFmt: fmtMoney(abertaAPI.valor_fatura),
					asaas: abertaAPI.id_cobranca_asaas,
					atrasada: vencida,
					linkBoleto: abertaAPI.link_pdf_boleto,
					linkCheckout: abertaAPI.link_checkout,
					pagto_recorrente: abertaAPI.pagto_recorrente,
					credit_card_number: abertaAPI.credit_card_number,
				});
			} else {
				// Sem fatura aberta
				setFaturaAtual(null);
			}

			// Anteriores = todas que não são 'A' (ou a própria abertaAPI reclassificada não entra aqui)
			const anteriores = ordenadas.filter((f) => f.status !== "A");
			setFaturasAnteriores(
				anteriores.map((f) => ({
					id: f.id_fatura,
					id_empresa: f.id_empresa,
					statusRaw: f.status,
					status: statusLabel(f.status),
					mes: capitalizeFirstLetter(mesAnoLongo(f.data_vencimento)),
					total: f.valor_fatura,
					totalFmt: fmtMoney(f.valor_fatura),
					vencimento: fmtDate(f.data_vencimento),
					pagoEm: fmtDate(f.data_pagto),
					asaas: f.id_cobranca_asaas,
				})),
			);

			// Itens
			const itensTabela = itens.map((i) => ({
				id: i.id_empresa_itens,
				produto: i.nome,
				valor: Number(i.valor ?? 0),
				valor_desconto: Number(i.valor_desconto ?? 0),
				acrescimo: Number(i.acrescimo ?? 0),
				totalItem: Number(
					i.valor_pedido ?? Number(i.valor ?? 0) - Number(i.valor_desconto ?? 0) + Number(i.acrescimo ?? 0),
				),
			}));
			setDados(itensTabela);
		} catch (error) {
			console.error("Erro ao buscar dados:", error);
		} finally {
			setCarregando(false);
		}
	};

	const formatarValor = (valor, formato) => (formato === "currency" ? fmtMoney(Number(valor)) : valor);

	const downloadInvoice = () => {
		if (!faturaAtual) return;

		if (faturaAtual.linkCheckout) {
			window.open(faturaAtual.linkCheckout, "_blank");
		}
	};

	const fetchInvoiceId = async (id_fatura) => {
		try {
			let invoiceData;

			//se veio do form manager busca passando o id_empresa
			if (isFormManager) {
				const { data } = await reqEmpresaFatura.listIdInvoiceAndIdEnterprise(
					id_fatura,
					faturasAnteriores[0]?.id_empresa || 0,
				);
				invoiceData = data;
			} else {
				const { data } = await reqEmpresaFatura.listId(id_fatura);
				invoiceData = data;
			}

			if (isEmptyObj(invoiceData?.faturas)) {
				throw new Error("Fatura não encontrada.");
			}

			handlePdf(invoiceData);
		} catch (error) {
			toast.error("Erro ao baixar a fatura." + (error?.message || ""));
		}
	};

	const payInvoice = async () => {
		if (isFormManager) {
			toast.warning("Operação não autorizada para o ID 1");
			return;
		}

		if (!faturaAtual) return;

		try {
			setLoading(true);
			await reqBillingAsaas.payWithCard({ id_cobranca_asaas: faturaAtual.id_cobranca_asaas });
			toast.success("Pagamento processado com sucesso.");
			window.location.reload();
		} catch (error) {
			toast.error("Erro ao processar o pagamento." + (error?.message || ""));
		} finally {
			setLoading(false);
		}
	};

	const handleOpenFormCard = () => {
		if (isFormManager) {
			toast.warning("Operação não autorizada para o ID 1");
			return;
		}

		const event = new CustomEvent("ModalFormCreditCard");
		window.dispatchEvent(event);
	};

	return (
		<div className="mt-12">
			<ToastContainer />
			<Box className="mt-4 mb-4" sx={{ display: "grid", gap: 2 }}>
				<Paper
					elevation={1}
					sx={{
						p: 2.5,
						display: "grid",
						gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
						alignItems: "center",
						borderLeft:
							faturaAtual?.statusRaw === "V"
								? "4px solid #ef4444" // vermelha se atrasada
								: faturaAtual
									? "4px solid #f59e0b" // amarela se aberta em dia
									: "4px solid #94a3b8", // cinza se não há
					}}
				>
					<Box>
						<Typography variant="h6" sx={{ fontWeight: 600 }}>
							{faturaAtual ? `Fatura ID:${faturaAtual.id} — ${faturaAtual.mesExibicao}` : "Fatura"}
						</Typography>

						{faturaAtual ? (
							<>
								<Typography variant="body2" color="text.secondary">
									Vencimento: {faturaAtual.vencimento}
									{faturaAtual.atrasada && " • (Atrasada)"}
								</Typography>
								{faturaAtual.pagoEm && (
									<Typography variant="body2" color="text.secondary">
										Pagamento: {faturaAtual.pagoEm}
									</Typography>
								)}
								<Typography variant="body2" color="text.secondary">
									Valor: {fmtMoney(faturaAtual.valor)}
								</Typography>
							</>
						) : (
							<Typography variant="body2" color="text.secondary">
								No momento não há fatura aberta. Assim que houver, ela aparecerá aqui.
							</Typography>
						)}
					</Box>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							justifyContent: { xs: "flex-start", sm: "flex-end" },
							mt: { xs: 1.5, sm: 0 },
						}}
					>
						<div>
							<Chip
								label={faturaAtual ? faturaAtual.status : "Fatura Indisponível"}
								color={faturaAtual ? statusColor(faturaAtual.statusRaw) : "default"}
								variant="outlined"
							/>
							{/* {faturaAtual?.pagto_recorrente !== "CARTÃO CRÉDITO" ? (
								<IconButton color="primary" disabled={!faturaAtual} onClick={downloadInvoice}>
									<DownloadIcon />
								</IconButton>
							) : (
								<IconButton color="success" disabled={!faturaAtual || faturaAtual.statusRaw !== "A"}>
									<PaymentIcon />
								</IconButton>
							)} */}

							<div className="flex justify-end flex-col w-full mt-3">
								{faturaAtual?.pagto_recorrente === "CARTÃO CRÉDITO" ? (
									<>
										{faturaAtual?.statusRaw === "V" && (
											<BtnCustom
												className="btn btn-outline-danger btn-sm Flex items-center flex justify-center gap-3"
												onClick={() => payInvoice()}
												isSubmitting={loading}
												text={"Pagar agora"}
											>
												{/* <PaymentIcon /> */}
											</BtnCustom>
										)}
										{/* <p className=" mt-3">Cobrança Recorrente</p> */}
										<p className="text-xs text-gray-600 mt-3">{`Cartão atual - Final ${faturaAtual?.credit_card_number}`}</p>
										<button
											className="btn btn-primary btn-sm gap-1 Flex items-center mt-1 flex"
											onClick={() => handleOpenFormCard()}
											disabled={loading}
										>
											<PaymentIcon />
											Alterar dados do cartão
										</button>
									</>
								) : faturaAtual ? (
									<button
										className="btn btn-primary btn-sm gap-3 flex items-center mt-3"
										onClick={() => downloadInvoice()}
										disabled={loading}
									>
										Gerar Pagamento
									</button>
								) : null}
							</div>
						</div>
					</Box>
					{faturaAtual?.statusRaw === "V" && faturaAtual?.pagto_recorrente === "CARTÃO CRÉDITO" && (
						<Alert severity="warning" sx={{ mt: 3 }}>
							⚠️ <strong>Atenção:</strong> Não foi possível fazer a cobrança automática do pagamento.
							Clique em pagar agora para realizar o pagamento manualmente.
						</Alert>
					)}
					{faturaAtual?.pagto_recorrente === "BOLETO" && (
						<Alert severity="warning" sx={{ mt: 3 }}>
							⚠️ <strong>Atenção:</strong> "Se sua licença está expirada. Após realizar o pagamento,
							aguarde alguns instantes e recarregue a página para ativar novamente o acesso."
						</Alert>
					)}
				</Paper>
			</Box>

			{/* Tabela */}
			<TableContainer component={Paper} className="shadow rounded-lg">
				<Table className="border-separate" style={{ borderSpacing: "0 8px" }}>
					<TableHead>
						<TableRow sx={{ backgroundColor: "#f9fafb" }}>
							{opcoes.colunasTabela.map((col) => (
								<TableCell
									key={col.key}
									align={col.align}
									sx={{
										width: col.width || "auto",
										fontWeight: "bold",
										color: "#555",
										borderBottom: "2px solid #eee",
									}}
								>
									{col.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>

					<TableBody>
						{dados.map((item, idx) => (
							<TableRow
								key={item.id ?? idx}
								sx={{
									backgroundColor: idx % 2 === 0 ? "#fff" : "#f6f6f6",
									"&:hover": { backgroundColor: "#f1f5f9" },
								}}
							>
								{opcoes.colunasTabela.map((col) => (
									<TableCell
										key={`${item.id}-${col.key}`}
										align={col.align}
										sx={{ borderBottom: "1px solid #f0f0f0" }}
									>
										{col.key === "produto"
											? item.produto
											: formatarValor(item[col.key], col.format)}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>

					<TableFooter>
						<TableRow sx={{ backgroundColor: "#f1f5f9" }}>
							<TableCell className="font-bold text-right">Totais:</TableCell>
							<TableCell align="right" className="font-bold">
								{fmtMoney(dados.reduce((a, i) => a + i.valor, 0))}
							</TableCell>
							<TableCell align="right" className="font-bold" sx={{ color: "red" }}>
								-{fmtMoney(dados.reduce((a, i) => a + i.valor_desconto, 0))}
							</TableCell>
							<TableCell align="right" className="font-bold" sx={{ color: "green" }}>
								+{fmtMoney(dados.reduce((a, i) => a + i.acrescimo, 0))}
							</TableCell>
							<TableCell align="right" className="font-bold">
								{fmtMoney(dados.reduce((a, i) => a + i.totalItem, 0))}
							</TableCell>
						</TableRow>
					</TableFooter>

					{carregando && (
						<TableFooter>
							<TableRow>
								<TableCell colSpan={opcoes.colunasTabela.length}>
									<LinearProgress variant="indeterminate" />
								</TableCell>
							</TableRow>
						</TableFooter>
					)}

					{!carregando && dados.length === 0 && (
						<TableBody>
							<TableRow>
								<TableCell colSpan={opcoes.colunasTabela.length} className="italic text-gray-400">
									Nenhum registro encontrado.
								</TableCell>
							</TableRow>
						</TableBody>
					)}
				</Table>
			</TableContainer>

			{/* Faturas anteriores (grid de cards simples) */}
			<Box className="mt-6">
				<Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
					Faturas Anteriores
				</Typography>

				{faturasAnteriores.length === 0 ? (
					<Paper elevation={0} sx={{ p: 2, color: "text.secondary" }}>
						Nenhuma fatura anterior encontrada.
					</Paper>
				) : (
					<Box
						sx={{
							display: "grid",
							gap: 2,
							gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
						}}
					>
						{faturasAnteriores.map((f) => (
							<Paper
								key={f.id}
								elevation={1}
								sx={{
									p: 2,
									display: "grid",
									gridTemplateRows: "auto 1fr auto",
									borderLeft:
										f.statusRaw === "P"
											? "4px solid #22c55e"
											: f.statusRaw === "V"
												? "4px solid #ef4444"
												: "4px solid #94a3b8",
								}}
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										mb: 1,
									}}
								>
									<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
										{f.mes}
									</Typography>
									<Chip
										label={f.status}
										color={statusColor(f.statusRaw)}
										size="small"
										variant="outlined"
									/>
								</Box>

								<Box sx={{ color: "text.secondary", display: "grid", gap: 0.5 }}>
									<Typography variant="body2">Vencimento: {f.vencimento}</Typography>
									{f.pagoEm && <Typography variant="body2">Pago em: {f.pagoEm}</Typography>}
									<Typography variant="body2" sx={{ fontWeight: 600 }}>
										Total: {f.totalFmt}
									</Typography>
								</Box>

								<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
									<IconButton
										color="primary"
										size="small"
										aria-label="Baixar boleto/recibo"
										onClick={() => {
											fetchInvoiceId(f.id);
										}}
									>
										<DownloadIcon fontSize="small" />
									</IconButton>
								</Box>
							</Paper>
						))}
					</Box>
				)}
			</Box>
			<ModalMain ident={"ModalFormCreditCard"}>
				<FormCreditCard onCloseModal={handleOpenFormCard} />
			</ModalMain>
		</div>
	);
};

export default InvoiceViewer;
